/**
 * React Query hooks for the rbac slice — ported from
 * `clario-frontend/src/services/rbac/rbac.query.ts`.
 *
 * Notes vs the source:
 *   - The original imported a Redux-derived `queryClient` from
 *     `@/services/react-query/queryClient`. Wave 3 agents replaced these with
 *     the `useQueryClient` hook, which is the canonical react-query pattern.
 *   - `useAuth()` now comes from `@clarix/contexts` (the compatibility shim
 *     over `@clarix/auth-store`). `refreshPermissions` there is a no-op until
 *     the permissions store is ported, but call sites stay so the API
 *     surface matches the legacy implementation.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from '@clarix/ui';
import { getApiErrorMessage } from '@clarix/utils';
import { useAuth } from '@clarix/contexts';

import { rbacApi } from './rbac.api';
import { rbacQueryKeys, projectTeamPermissionsKey } from './rbac.query-keys';
import type {
  CreateTemplatePayload,
  UpdateTemplatePayload,
  AssignUserTemplatePayload,
  UpdateUserOverridesPayload,
  BulkAssignTemplatePayload,
  SetProjectPermissionPayload,
  GrantTempElevationPayload,
  AuditLogFilters,
} from './rbac.types';

// ─── Stale Time Constants ────────────────────────────────────────
const STALE_5_MIN = 5 * 60 * 1000;
const STALE_2_MIN = 2 * 60 * 1000;
const STALE_30_SEC = 30 * 1000;

// ─── Templates ──────────────────────────────────────────────────

export function useTemplates(params?: { active?: boolean }) {
  return useQuery({
    queryKey: rbacQueryKeys.templates.all,
    queryFn: () => rbacApi.listTemplates(params),
    staleTime: STALE_5_MIN,
  });
}

export function useTemplate(id: string | null) {
  return useQuery({
    queryKey: rbacQueryKeys.templates.detail(id ?? ''),
    queryFn: () => {
      if (!id) throw new Error('useTemplate requires a non-null id');
      return rbacApi.getTemplate(id);
    },
    enabled: !!id,
    staleTime: STALE_5_MIN,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTemplatePayload) => rbacApi.createTemplate(payload),
    onSuccess: () => {
      toast.success('Template created successfully');
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.templates.all });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to create template'));
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  const { refreshPermissions } = useAuth();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTemplatePayload }) =>
      rbacApi.updateTemplate(id, data),
    onSuccess: () => {
      toast.success('Template updated successfully');
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.templates.all });
      // Refresh current user's permissions in case their template was updated
      void refreshPermissions();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update template'));
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rbacApi.deleteTemplate(id),
    onSuccess: () => {
      toast.success('Template deleted successfully');
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.templates.all });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to delete template'));
    },
  });
}

// ─── User Permissions ───────────────────────────────────────────

export function useAllUserPermissions(params?: { search?: string; template_ids?: string }) {
  return useQuery({
    queryKey: rbacQueryKeys.allUserPermissions(params),
    queryFn: () => rbacApi.listAllUserPermissions(params),
    staleTime: STALE_5_MIN,
    placeholderData: keepPreviousData,
  });
}

export function useUserPermission(userId: string | null) {
  return useQuery({
    queryKey: rbacQueryKeys.userPermission(userId ?? ''),
    queryFn: () => {
      if (!userId) throw new Error('useUserPermission requires a non-null userId');
      return rbacApi.getUserPermission(userId);
    },
    enabled: !!userId,
    staleTime: STALE_5_MIN,
  });
}

export function useAssignUserTemplate() {
  const queryClient = useQueryClient();
  const { refreshPermissions, user } = useAuth();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: AssignUserTemplatePayload }) =>
      rbacApi.assignUserTemplate(userId, data),
    onSuccess: (_data, variables) => {
      toast.success('Template assigned successfully');
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.userPermission(variables.userId) });
      queryClient.invalidateQueries({ queryKey: ['rbac', 'all-user-permissions'] });
      queryClient.invalidateQueries({
        queryKey: rbacQueryKeys.permissionMatrix(variables.userId),
      });
      // If the admin is changing their own template, refresh immediately
      if (variables.userId === user?.id) void refreshPermissions();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to assign template'));
    },
  });
}

export function useUpdateUserOverrides() {
  const queryClient = useQueryClient();
  const { refreshPermissions, user } = useAuth();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserOverridesPayload }) =>
      rbacApi.updateUserOverrides(userId, data),
    onSuccess: (_data, variables) => {
      toast.success('Overrides updated successfully');
      queryClient.invalidateQueries({ queryKey: rbacQueryKeys.userPermission(variables.userId) });
      queryClient.invalidateQueries({ queryKey: ['rbac', 'all-user-permissions'] });
      queryClient.invalidateQueries({
        queryKey: rbacQueryKeys.permissionMatrix(variables.userId),
      });
      // If overrides changed for current user, refresh immediately
      if (variables.userId === user?.id) void refreshPermissions();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update overrides'));
    },
  });
}

export function useDeleteUserPermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => rbacApi.deleteUserPermission(userId),
    onSuccess: () => {
      toast.success('Permission assignment removed');
      queryClient.invalidateQueries({ queryKey: ['rbac', 'all-user-permissions'] });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to remove permission assignment'));
    },
  });
}

export function useBulkAssignTemplate() {
  const queryClient = useQueryClient();
  const { refreshPermissions } = useAuth();
  return useMutation({
    mutationFn: (data: BulkAssignTemplatePayload) => rbacApi.bulkAssignTemplate(data),
    onSuccess: () => {
      toast.success('Template assigned to selected users');
      queryClient.invalidateQueries({ queryKey: ['rbac', 'all-user-permissions'] });
      // Current user may be in the bulk list — refresh to be safe
      void refreshPermissions();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to bulk assign template'));
    },
  });
}

// ─── Permission Matrix ──────────────────────────────────────────

export function usePermissionMatrix(userId: string | null, projectId?: string) {
  return useQuery({
    queryKey: rbacQueryKeys.permissionMatrix(userId ?? '', projectId),
    queryFn: () => {
      if (!userId) throw new Error('usePermissionMatrix requires a non-null userId');
      return rbacApi.getPermissionMatrix(userId, projectId);
    },
    enabled: !!userId,
    staleTime: STALE_5_MIN,
    placeholderData: keepPreviousData,
  });
}

// ─── Project Permissions ────────────────────────────────────────

/** Fetch all permissions for a specific project */
export function useProjectPermissions(projectId: string) {
  return useQuery({
    queryKey: rbacQueryKeys.projectPermissions(projectId),
    queryFn: () => rbacApi.listProjectPermissions(projectId),
    enabled: !!projectId,
    staleTime: STALE_5_MIN,
  });
}

export function useUpdateProjectOverrides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      userId,
      overrides,
    }: {
      projectId: string;
      userId: string;
      overrides: Record<string, boolean>;
    }) => rbacApi.updateProjectOverrides(projectId, userId, overrides),
    onSuccess: (_data, variables) => {
      toast.success('Project overrides updated');
      queryClient.invalidateQueries({
        queryKey: rbacQueryKeys.projectPermissions(variables.projectId),
      });
      // Also refresh the team-tab permissions list (uses a separate query key)
      queryClient.invalidateQueries({
        queryKey: projectTeamPermissionsKey(variables.projectId),
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to update project overrides'));
    },
  });
}

export function useSetProjectPermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: SetProjectPermissionPayload }) =>
      rbacApi.setProjectPermission(projectId, data),
    onSuccess: (_data, variables) => {
      toast.success('Project permission updated');
      queryClient.invalidateQueries({
        queryKey: rbacQueryKeys.projectPermissions(variables.projectId),
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to set project permission'));
    },
  });
}

export function useRemoveProjectPermission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, userId }: { projectId: string; userId: string }) =>
      rbacApi.removeProjectPermission(projectId, userId),
    onSuccess: (_data, variables) => {
      toast.success('Project permission removed');
      queryClient.invalidateQueries({
        queryKey: rbacQueryKeys.projectPermissions(variables.projectId),
      });
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to remove project permission'));
    },
  });
}

// ─── Temp Elevations ────────────────────────────────────────────

/** Convenience alias — fetches all elevations for the Elevations tab */
export function useElevations(params?: { user_id?: string; project_id?: string; status?: string }) {
  return useQuery({
    queryKey: rbacQueryKeys.elevations({ ...params }),
    queryFn: () => rbacApi.listTempElevations(params),
    staleTime: STALE_2_MIN,
  });
}

/** Convenience alias for granting temp elevation */
export function useGrantElevation() {
  const queryClient = useQueryClient();
  const { refreshPermissions } = useAuth();
  return useMutation({
    mutationFn: (data: GrantTempElevationPayload) => rbacApi.grantTempElevation(data),
    onSuccess: () => {
      toast.success('Temporary elevation granted');
      // Force immediate refetch so the elevations list updates in real time
      queryClient.refetchQueries({ queryKey: ['rbac', 'elevations'] });
      void refreshPermissions();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to grant elevation'));
    },
  });
}

/** Convenience alias for revoking temp elevation */
export function useRevokeElevation() {
  const queryClient = useQueryClient();
  const { refreshPermissions } = useAuth();
  return useMutation({
    mutationFn: (id: string) => rbacApi.revokeTempElevation(id),
    onSuccess: () => {
      toast.success('Elevation revoked');
      queryClient.refetchQueries({ queryKey: ['rbac', 'elevations'] });
      void refreshPermissions();
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err, 'Failed to revoke elevation'));
    },
  });
}

// ─── Audit Logs ─────────────────────────────────────────────────

export function useAuditLogs(params?: AuditLogFilters) {
  return useQuery({
    queryKey: rbacQueryKeys.auditLogs({
      actor_id: params?.actor_id,
      target_user_id: params?.target_user_id,
      action: params?.action,
      page: params?.page,
      page_size: params?.page_size,
    }),
    queryFn: () => rbacApi.listAuditLogs(params),
    staleTime: STALE_30_SEC,
  });
}
